import qs from 'qs';
import { parseLayerAndOptions } from '../layers/rendering/layerUrl.ts';
import {
  type ILayerAttribute,
  type LayerMetadataResult,
} from './LayerMetadata.ts';

interface IArcGISFieldResponse {
  name?: unknown;
  alias?: unknown;
  type?: unknown;
}

/**
 * Normalizza l'enum `geometryType` di ArcGIS REST in un'etichetta
 * leggibile e agnostica rispetto al servizio. I tipi non mappati
 * mantengono il valore grezzo del servizio.
 */
const GEOMETRY_TYPE_LABELS: Record<string, string> = {
  esriGeometryPoint: 'Punti',
  esriGeometryMultipoint: 'Punti multipli',
  esriGeometryPolyline: 'Linee',
  esriGeometryPolygon: 'Poligoni',
  esriGeometryEnvelope: 'Rettangoli',
};

function parseArcGISField(field: IArcGISFieldResponse): ILayerAttribute | null {
  if (typeof field?.name !== 'string' || field.name.length === 0) return null;

  return {
    name: field.name,
    alias: typeof field.alias === 'string' ? field.alias : undefined,
    type: typeof field.type === 'string' ? field.type : undefined,
  };
}

/**
 * Interpreta la risposta `?f=json` di un endpoint ArcGIS REST.
 * ArcGIS risponde HTTP 200 anche in caso di errore (body `{error: {...}}`).
 */
export function parseArcGISLayerMetadataResponse(
  body: unknown,
): LayerMetadataResult {
  if (typeof body !== 'object' || body === null) {
    return { status: 'invalid-response' };
  }

  const response = body as Record<string, unknown>;

  if (response.error) return { status: 'invalid-response' };

  if (Array.isArray(response.fields)) {
    const attributes = response.fields
      .map(field => parseArcGISField(field as IArcGISFieldResponse))
      .filter((attribute): attribute is ILayerAttribute => attribute !== null);

    return {
      status: 'ok',
      metadata: {
        attributes,
        geometryType:
          typeof response.geometryType === 'string'
            ? (GEOMETRY_TYPE_LABELS[response.geometryType] ??
              response.geometryType)
            : undefined,
      },
    };
  }

  // Un URL alla root del servizio (es. .../MapServer) espone `layers`
  // ma non `fields`: serve l'URL di un singolo layer.
  if (Array.isArray(response.layers)) return { status: 'service-root' };

  return { status: 'invalid-response' };
}

/**
 * Introspezione di un layer ArcGIS REST tramite `{url}?f=json`.
 * Non lancia mai: ogni errore è mappato in un `LayerMetadataResult`.
 */
export async function fetchArcGISLayerMetadata(
  rawUrl: string,
): Promise<LayerMetadataResult> {
  const { url, options } = parseLayerAndOptions(rawUrl);

  // Preserva i parametri configurati nell'URL (es. `token` per servizi
  // protetti) e forza `f=json` per la risposta dei metadati.
  const query = qs.stringify({ ...options, f: 'json' });

  let response: Response;
  try {
    response = await fetch(`${url}?${query}`);
  } catch {
    return { status: 'unreachable' };
  }

  if (!response.ok) return { status: 'unreachable' };

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  return parseArcGISLayerMetadataResponse(body);
}
